import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, Optional } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { Observable, combineLatest, filter, map, takeUntil } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { Select, ofActionSuccessful } from '@ngxs/store';

import { DataGridComponent } from '@app/shared/components/smart-data-grid/data-grid/data-grid.component';
import { DriverReadListDataGridColumnDef, GetTeamMessageReceiverDriversSearchByList } from '@app/pages/team-message/team-message.constant';
import { TeamMessage, TeamMessageRead } from '@app/shared/classes/team-message';
import { SmartSearchFormComponent } from '@app/shared/components/smart-search-form/smart-search-form.component';
import { NgxsActionName, NgxsFormPath } from '@app/shared/constants';
import { PageBasedComponent } from '@app/pages/page.based.component';
import { SearchByItem, WebApiResponse, WebApiResponseBase } from '@app/shared/classes/general';
import { GetTeamMessageRead, GetTeamMessageReadForExport } from '@app/shared/actions/app-data.action';
import { AppDataState } from '@app/shared/stores/app-data.state';
import { MessageDataComponent } from '@app/shared/components/message-data/message-data.component';
import { CommonPageFilterSetting } from '@app/shared/models/common-data.model';
import { TerminateTask } from '@app/shared/actions/general.action';
import { ExcelExportService } from '@app/shared/services/excel-export-service';
import { inject } from "@angular/core";
import { LocaleDateTime } from "@app/shared/pipes/locale-date-time";

@Component({
  selector: 'app-receiver-driver-list',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslateModule, MatDialogModule, MessageDataComponent, SmartSearchFormComponent, DataGridComponent],
  templateUrl: './receiver-driver-list.component.html',
  styleUrl: './receiver-driver-list.component.css'
})
export class ReceiverDriverListComponent extends PageBasedComponent {
  excelExportService = inject(ExcelExportService);
  dataGridColumnDef = DriverReadListDataGridColumnDef;
  formPath = NgxsFormPath.TEAM_MESSAGE_READ_SEARCH_FORM;

  dataSource$: Observable<TeamMessageRead[]>;
  searchByList$: Observable<SearchByItem[]>;

  @Select(AppDataState.taskExecuting(NgxsActionName.GET_TEAM_MESSAGE_READ)) searching$: Observable<boolean>;
  @Select(AppDataState.taskResponse(NgxsActionName.GET_TEAM_MESSAGE_READ)) response$: Observable<WebApiResponse<TeamMessageRead[]>>;
  @Select(AppDataState.taskError(NgxsActionName.GET_TEAM_MESSAGE_READ)) errorResponse$: Observable<WebApiResponseBase>;

  dataSourceForExport$: Observable<TeamMessageRead[]>;

  @Select(AppDataState.taskExecuting(NgxsActionName.GET_TEAM_MESSAGE_READ_FOR_EXPORT)) searchingForExport$: Observable<boolean>;
  @Select(AppDataState.taskResponse(NgxsActionName.GET_TEAM_MESSAGE_READ_FOR_EXPORT)) responseForExport$: Observable<WebApiResponse<TeamMessageRead[]>>;
  @Select(AppDataState.taskError(NgxsActionName.GET_TEAM_MESSAGE_READ_FOR_EXPORT)) errorResponseForExport$: Observable<WebApiResponseBase>;

  constructor(@Optional() @Inject(MAT_DIALOG_DATA) public teamMessage: TeamMessage) {
    super();
  }

  override ngOnInit() {
    super.ngOnInit();

    this.errorMessage$ = this.errorResponse$.pipe(map(errorResponse => this.getErrorMessages([errorResponse])));

    this.searchByList$ = combineLatest([this.currentTerminalDropDowns$, this.currentSubRegions$]).pipe(map(c => GetTeamMessageReceiverDriversSearchByList(c)));
    this.dataSource$ = this.response$.pipe(filter(Boolean), map(c => c.data));
    this.totalCount$ = this.response$.pipe(filter(Boolean), map(c => c.totalCount));

    this.dataSourceForExport$ = this.responseForExport$.pipe(filter(Boolean), map(c => c.data));    

    this.dataSourceForExport$.pipe(takeUntil(this.destroy$)).subscribe(res => {
      const rows = res.map(row => ({
        apexId: row.apexId,
        fullName: row.fullName,
        terminalId: row.terminalId,
        terminalName: row.terminalName,
        subRegionName: row.subRegionName,
        country: row.country,
        readTime: new LocaleDateTime().transform(row.readTime)
      }));
      this.excelExportService.exportExcelFile(rows);
    });


  }

  override ngAfterViewInit(): void {
    this.onSearch();
  }

  override onSearch = () => {
    this.store.dispatch(new GetTeamMessageRead(this.teamMessage.teamMessage_Id));
  }

  onExport = () => {    
    this.store.dispatch(new GetTeamMessageReadForExport(this.teamMessage.teamMessage_Id, null));        
  }

  onSearchWithExistedPageFilter = () => {
    this.store.dispatch(new GetTeamMessageRead(this.teamMessage.teamMessage_Id, null));
  }

  onGetData = (info: CommonPageFilterSetting) => {
    info.functionName = NgxsActionName.GET_TEAM_MESSAGE_READ;
    this.store.dispatch(new GetTeamMessageRead(this.teamMessage.teamMessage_Id, info));
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.store.dispatch(new TerminateTask(NgxsActionName.GET_TEAM_MESSAGE_READ));
    this.store.dispatch(new TerminateTask(NgxsActionName.GET_TEAM_MESSAGE_READ_FOR_EXPORT));
  }
}
